/**
 * Instance Detail service — MOCK-FIRST (CT-3897).
 *
 * A concrete deployment of a provider (its own credentials, capability toggles,
 * access and trail). Synthesized from the same connected-provider set as
 * `providerService`. Platform envelope, snake_case, `TODO(CT-3904 / backend)`.
 */

import { envelope, notFoundEnvelope } from '../shared/utils/mockServiceHelpers.js';
import { CONNECTED_PROVIDERS, isProviderDisconnected } from './connectedProvidersCatalog.js';

const parseIndex = (providerSlug, instanceId) => {
  const m = instanceId?.match(/-inst-(\d+)$/);
  return m ? Number(m[1]) - 1 : 0;
};

// [name, type, studio, uses, minutesAgo]. Callers paginate client-side like
// logs/audit; last_used is relative to "now" so the timestamp column stays fresh.
const USAGE_CONSUMERS = [
  ['Support Triage Agent', 'agent', 'agentic', 640, 12],
  ['Nightly Sync', 'workflow', 'workforce', 300, 41],
  ['Incident Commander', 'agent', 'workforce', 288, 55],
  ['Danilo Petrov', 'user', 'governance', 92, 80],
  ['Daily revenue report', 'workflow', 'workforce', 210, 120],
  ['Knowledge Indexer', 'agent', 'governance', 176, 180],
  ['María González', 'user', 'governance', 84, 210],
  ['CS ticket auto-routing', 'workflow', 'governance', 154, 260],
  ['Onboarding Bot', 'agent', 'agentic', 133, 320],
  ['Thomas González', 'user', 'workforce', 61, 400],
  ['PR review notification', 'workflow', 'workforce', 48, 540],
  ['Renewal Risk', 'agent', 'agentic', 44, 700],
  ['Naomi Park', 'user', 'governance', 29, 960],
  ['Incident response', 'workflow', 'workforce', 22, 1100],
  ['Diana Pérez', 'user', 'agentic', 18, 1500],
];

// [capability, calls_30d, success_rate]. Ranked desc by calls in the response so
// the breakdown table reads most-used first without a client-side sort.
const CAPABILITY_BREAKDOWN = [
  ['send_message', 1240, 99.2],
  ['read_records', 980, 97.5],
  ['run_query', 610, 92.1],
  ['create_ticket', 420, 98.8],
  ['list_objects', 305, 88.4],
  ['update_record', 210, 99.5],
  ['schedule_meeting', 96, 78.3],
];

const DAY_MS = 24 * 60 * 60 * 1000;
const USAGE_PERIODS = [7, 30, 90];

// Deterministic daily call volume for the time-series chart — a per-instance
// seed + day index, with a weekend dip so the shape reads like real traffic.
const dayVolume = (seed, dayIdx, weekday) => {
  const base = 55 + (Math.abs(seed) % 45);
  const wiggle = Math.abs(((seed >> 3) ^ (dayIdx * 0x9e3779b1)) % 50);
  const weekend = weekday === 0 || weekday === 6 ? 0.4 : 1;
  return Math.max(0, Math.round((base + wiggle) * weekend));
};

/**
 * Per-instance usage: stats + daily call-volume series + capability breakdown +
 * consumers (CT-4193). `period` (7/30/90 days) sizes the series. Consumers are
 * newest-used first; capabilities are ranked desc by calls. Stats derive from the
 * capability calls so the tiles and the breakdown table always agree.
 * TODO(CT-3904 / backend): replace with `GET /v1/workspace/integrations/:slug/instances/:id/usage`.
 */
export const getInstanceUsage = (instanceId, { period = 30 } = {}) => {
  const now = Date.now();
  const days = USAGE_PERIODS.includes(period) ? period : 30;
  let seed = 0;
  for (const c of instanceId ?? '') seed = (seed * 31 + c.charCodeAt(0)) | 0;
  const drift = Math.abs(seed) % 7;
  const count = USAGE_CONSUMERS.length - (Math.abs(seed) % 3);

  const series = Array.from({ length: days }, (_, k) => {
    const dayIdx = days - 1 - k; // oldest (left) → today (right)
    const date = new Date(now - dayIdx * DAY_MS);
    return { day: date.toISOString().slice(0, 10), calls: dayVolume(seed, dayIdx, date.getUTCDay()) };
  });
  const at = (minutesAgo) => new Date(now - (minutesAgo + drift) * 60_000).toISOString();
  const consumers = USAGE_CONSUMERS.slice(0, count).map(([name, type, studio, uses, minutesAgo], idx) => ({
    id: `uc-${idx + 1}`,
    name,
    type,
    studio,
    uses,
    last_used: at(minutesAgo),
  }));

  const capCount = CAPABILITY_BREAKDOWN.length - (Math.abs(seed) % 2);
  const capabilities = CAPABILITY_BREAKDOWN.slice(0, capCount).map(([name, calls, success_rate], idx) => ({
    id: `cap-${idx + 1}`,
    name,
    calls,
    success_rate,
  }));

  const capabilityCalls = capabilities.reduce((s, c) => s + c.calls, 0);
  const weightedSuccess = capabilities.reduce((s, c) => s + c.success_rate * c.calls, 0);
  const baseSuccess = capabilityCalls ? weightedSuccess / capabilityCalls : 0;

  // Period-scoped tiles (7/30/90): call volume is the series sum, and the rates
  // shift slightly per window so switching period visibly updates every metric
  // tile — not just the chart. Active consumers is the on-record directory count
  // (matches the table), so it stays stable across windows.
  const successDelta = { 7: -0.8, 30: 0, 90: 0.4 };
  const latencyDelta = { 7: 25, 30: 0, 90: -15 };
  const totalCalls = series.reduce((s, d) => s + d.calls, 0);
  const successRate = Math.round((baseSuccess + (successDelta[days] ?? 0)) * 10) / 10;
  const avgLatency = Math.max(50, 220 + (Math.abs(seed) % 140) + (latencyDelta[days] ?? 0));

  return Promise.resolve(
    envelope({
      stats: {
        total_calls: totalCalls,
        active_consumers: consumers.length,
        success_rate: successRate,
        avg_latency_ms: avgLatency,
      },
      period: days,
      series,
      capabilities,
      consumers,
    }),
  );
};

// One tuple per call: [action, actor, status, studio, latency_ms, error, minutesAgo].
// Timestamps are relative to "now" (built in listInstanceLogs) so the date-range
// filter reads like the prototype instead of drifting past every fixed window.
const LOG_TEMPLATES = [
  ['send_message', 'Agent: Sales Triage Bot', 'success', 'agentic', 240, null, 2],
  ['read_records', 'Agent: Incident Commander', 'success', 'workforce', 399, null, 41],
  ['schedule_meeting', 'User: edgardo.s@contoso.com', 'timeout', 'agentic', 30000, 'Slow upstream response', 60],
  ['create_ticket', 'Network: Incident Response', 'success', 'governance', 384, null, 120],
  ['run_query', 'Workflow: Daily revenue report', 'rate_limited', 'workforce', 187, 'Rate limit exceeded', 130],
  ['list_users', 'Workflow: Daily revenue report', 'rate_limited', 'workforce', 210, 'API quota exceeded', 145],
  ['attach_file', 'System: retry-queue', 'success', 'agentic', 512, null, 160],
  ['list_objects', 'Agent: Knowledge Indexer', 'timeout', 'governance', 8000, 'DNS resolution failed', 180],
  ['update_record', 'User: maria.g@contoso.com', 'success', 'workforce', 176, null, 210],
  ['fetch_metrics', 'Workflow: Incident response', 'timeout', 'agentic', 8000, 'Provider unresponsive', 260],
  ['put_object', 'Agent: Onboarding Bot', 'success', 'governance', 288, null, 320],
  ['transition_status', 'User: naomi.p@contoso.com', 'success', 'workforce', 244, null, 380],
  ['search_documents', 'Agent: PR Reviewer', 'success', 'governance', 640, null, 420],
  ['send_dm', 'Agent: Onboarding Bot', 'success', 'agentic', 190, null, 480],
  ['create_pr', 'Workflow: PR review notification', 'success', 'workforce', 288, null, 540],
  ['read_channel_history', 'Agent: Knowledge Indexer', 'success', 'governance', 205, null, 620],
  ['add_comment', 'Workflow: CS ticket auto-routing', 'success', 'governance', 210, null, 700],
  ['assign_issue', 'User: thomas.g@contoso.com', 'failed', 'workforce', 470, 'Permission denied', 800],
  ['merge_pr', 'Agent: Eng Standup', 'success', 'agentic', 355, null, 960],
  ['close_issue', 'Agent: Incident Commander', 'failed', 'agentic', 620, 'Schema mismatch', 1100],
  ['create_pr', 'Workflow: PR review notification', 'success', 'workforce', 288, null, 1500],
  ['assign_issue', 'User: thomas.g@contoso.com', 'failed', 'workforce', 470, 'Permission denied', 60 * 26],
  ['put_object', 'Workflow: New hire', 'success', 'governance', 512, null, 60 * 24 * 5],
  ['transition_status', 'User: naomi.p@contoso.com', 'timeout', 'workforce', 9000, 'Network timeout', 60 * 24 * 8],
  ['list_objects', 'Agent: Renewal Risk', 'success', 'agentic', 133, null, 60 * 24 * 12],
  ['add_comment', 'Workflow: CS ticket auto-routing', 'success', 'governance', 210, null, 60 * 24 * 20],
  ['schedule_meeting', 'User: diana.p@contoso.com', 'success', 'workforce', 244, null, 60 * 24 * 40],
];

/**
 * Per-instance execution log (CT-4189) — one row per tool invocation, newest
 * first. Callers filter/paginate client-side (mirrors `useProviderAudit`); the
 * reference `now` for the date-range cutoff is captured at load in the hook.
 * TODO(CT-3904 / backend): replace with `GET /v1/workspace/integrations/:slug/instances/:id/logs`.
 */
export const listInstanceLogs = (instanceId) => {
  const now = Date.now();
  // Deterministic per-instance seed so two instances of the same provider don't
  // read as clones. Newest-first ordering is preserved: `drift` shifts every
  // timestamp uniformly, and we only trim the oldest rows — never reorder.
  let seed = 0;
  for (const c of instanceId ?? '') seed = (seed * 31 + c.charCodeAt(0)) | 0;
  const drift = Math.abs(seed) % 7; // minutes, applied to every row
  const count = LOG_TEMPLATES.length - (Math.abs(seed) % 5); // trim a few oldest
  const at = (minutesAgo) => new Date(now - (minutesAgo + drift) * 60_000).toISOString();
  const rows = LOG_TEMPLATES.slice(0, count).map(
    ([action, actor, status, studio, latency_ms, error, minutesAgo], idx) => ({
      id: `log-${idx + 1}`,
      timestamp: at(minutesAgo),
      action,
      actor,
      status,
      studio,
      latency_ms,
      error,
    }),
  );
  return Promise.resolve(envelope(rows, { total: rows.length }));
};

// One tuple per event: [event, actor, detail, minutesAgo]. Timestamps are
// relative to "now" so the date-range filter reads like the prototype.
const AUDIT_TEMPLATES = [
  ['credentials.test', 'Diana Pérez', 'Pre-deploy verification — all checks green', 60],
  ['capability.activated', 'Thomas González', 'Enabled search_documents tool', 120],
  ['access.granted', 'María González', 'Added Marketing workspace to instance availability', 240],
  ['mapping.updated', 'AIMS-OS (auto)', 'Synced 12 field mappings from provider schema', 400],
  ['instance.credentials_rotated', 'Danilo Petrov', 'Rotated API key after expiry warning', 800],
  ['capability.toggled', 'Maria Gomez', 'Disabled create_ticket for Workforce studio', 60 * 20],
  ['access.revoked', 'Thomas González', 'Removed Staging workspace from instance availability', 60 * 30],
  ['credentials.test', 'Diana Pérez', 'Scheduled credentials health check passed', 60 * 36],
  ['mapping.updated', 'AIMS-OS (auto)', 'Added custom_field_priority mapping', 60 * 48],
  ['capability.activated', 'Naomi Park', 'Enabled list_objects tool', 60 * 60],
  ['access.granted', 'Danilo Petrov', 'Added Support workspace to instance availability', 60 * 72],
  ['instance.created', 'Danilo Petrov', 'Instance provisioned as AIMS-managed', 60 * 24 * 40],
];

/**
 * Immutable per-instance governance trail (CT-4192) — one row per config change,
 * newest first. Callers filter/paginate client-side. The reference `now` for the
 * date-range cutoff is captured at load in the hook.
 * TODO(CT-3904 / backend): replace with `GET /v1/workspace/integrations/:slug/instances/:id/audit`.
 */
export const listInstanceAudit = (instanceId) => {
  const now = Date.now();
  // Deterministic per-instance seed so two instances don't read as clones.
  // Newest-first ordering is preserved: `drift` shifts every timestamp uniformly
  // and we only trim the oldest rows — never reorder.
  let seed = 0;
  for (const c of instanceId ?? '') seed = (seed * 31 + c.charCodeAt(0)) | 0;
  const drift = Math.abs(seed) % 7; // minutes, applied to every row
  const count = AUDIT_TEMPLATES.length - (Math.abs(seed) % 4); // trim a few oldest
  const at = (minutesAgo) => new Date(now - (minutesAgo + drift) * 60_000).toISOString();
  const rows = AUDIT_TEMPLATES.slice(0, count).map(([event, actor, detail, minutesAgo], idx) => ({
    id: `ia${idx + 1}`,
    timestamp: at(minutesAgo),
    event,
    actor,
    detail,
  }));
  return Promise.resolve(envelope(rows, { total: rows.length }));
};

/**
 * Full instance detail.
 * TODO(CT-3904 / backend): replace with `GET /v1/workspace/integrations/:slug/instances/:id`.
 */
export const getInstance = (providerSlug, instanceId) => {
  const base = CONNECTED_PROVIDERS[providerSlug];
  if (!base || isProviderDisconnected(providerSlug)) return Promise.resolve(notFoundEnvelope());
  const i = parseIndex(providerSlug, instanceId);
  if (i < 0 || i >= (base.instances?.length ?? 0)) return Promise.resolve(notFoundEnvelope());
  const status = base.instances[i];
  const isProd = i === 0;
  return Promise.resolve(
    envelope({
      instance_id: instanceId,
      name: `${base.name} — ${isProd ? 'Production' : 'Staging'}`,
      provider_slug: providerSlug,
      provider_name: base.name,
      official: base.official,
      status,
      managed_by: isProd ? 'aims' : 'byok',
      auth_method: base.auth,
      credential_expires_at:
        status === 'auth_expired'
          ? '2026-07-30T00:00:00.000Z'
          : status === 'auth_expiring'
            ? '2026-08-08T00:00:00.000Z'
            : null,
      last_run: '2026-08-02T18:20:00.000Z',
      connected_at: '2026-06-14T10:00:00.000Z',
      connected_by: { name: 'Danilo Petrov', initials: 'DP' },
      errors_24h: status === 'working' ? 0 : status === 'schema_drift' ? 4 : 1,
      instance_count: base.instances.length,
    }),
  );
};

const AUTH_FROM_PROVIDER = {
  oauth: 'oauth',
  api_key: 'apikey',
  key_pair: 'service',
};

/** Prototype `AUTH_METHOD_DETAIL` (CT-4194) — method copy + fields + actions. */
const AUTH_METHODS = {
  oauth: {
    id: 'oauth',
    label: 'OAuth 2.0',
    badge: 'Per-user identity',
    color: '#34d39c',
    desc: 'A real human signed in to the provider and authorized AIMS-OS to act on their behalf. Each action is attributable to that user; the provider can revoke at any time.',
    identity_note:
      "Actions run under the connecting user's identity. If they leave the org, this instance breaks until re-authenticated.",
    fields: [
      { key: 'authenticated_as', label: 'Authenticated as', value: 'Provider user (set at connection time)' },
      { key: 'token_type', label: 'Token type', value: 'Refresh token + short-lived access token' },
      { key: 'auto_refresh', label: 'Auto-refresh', value: 'Yes — AIMS rotates the access token on schedule' },
    ],
    actions: [
      { id: 'reauthenticate', variant: 'primary' },
      { id: 'test', variant: 'secondary' },
      { id: 'scopes', variant: 'secondary' },
    ],
  },
  apikey: {
    id: 'apikey',
    label: 'API token',
    badge: 'Long-lived secret',
    color: '#7ed3f7',
    desc: "A long-lived token issued by the provider. Used for headless / service automation where OAuth isn't practical. Anyone with the token can act as this instance.",
    identity_note: 'Actions are NOT attributable to a real user. Audit trails show the integration name, not a person.',
    fields: [
      { key: 'token_label', label: 'Token label', value: 'Production key' },
      { key: 'stored_at', label: 'Stored at', value: 'AIMS Secret Vault (encrypted at rest)' },
      { key: 'rotation', label: 'Rotation policy', value: 'Manual — admins must rotate periodically' },
    ],
    actions: [
      { id: 'rotate', variant: 'primary' },
      { id: 'test', variant: 'secondary' },
      { id: 'revoke', variant: 'secondary' },
    ],
  },
  service: {
    id: 'service',
    label: 'Service account',
    badge: 'Server-to-server',
    color: '#cda5ff',
    desc: 'A JSON key file from a service account. For server-to-server access where the integration acts as a system identity (e.g. Google Cloud, AWS IAM).',
    identity_note: 'Actions are attributable to the service account, not a human. Provider-side IAM controls what it can do.',
    fields: [
      { key: 'key_file', label: 'Key file', value: 'service-account-key.json (encrypted)' },
      { key: 'account', label: 'Project / Account', value: 'Provider-issued ID' },
      { key: 'iam', label: 'IAM permissions', value: 'Controlled at the provider (read-only here)' },
    ],
    actions: [
      { id: 'upload', variant: 'primary' },
      { id: 'test', variant: 'secondary' },
    ],
  },
};

/**
 * Per-instance authentication method + connection fields (CT-4194).
 * TODO(CT-3904 / backend): replace with `GET .../instances/:id/auth`.
 */
export const getInstanceAuth = (providerSlug, instanceId) => {
  const base = CONNECTED_PROVIDERS[providerSlug];
  if (!base || isProviderDisconnected(providerSlug)) return Promise.resolve(notFoundEnvelope());
  const i = parseIndex(providerSlug, instanceId);
  if (i < 0 || i >= (base.instances?.length ?? 0)) return Promise.resolve(notFoundEnvelope());
  const isProd = i === 0;
  const method = AUTH_METHODS[AUTH_FROM_PROVIDER[base.auth] ?? 'oauth'];
  return Promise.resolve(
    envelope({
      ...method,
      managed_by: isProd ? 'aims' : 'byok',
      connected_by: { name: 'Thomas González' },
      connected_at: new Date().toISOString(),
    }),
  );
};

/** Per-instance capability state (CT-4190) — mutable so toggles persist in-process. */
const buildCapabilitySeed = () => ({
  send_message: { id: 'send_message', type: 'tool', label: 'Send message', description: 'Post messages and alerts.', enabled: true, active_instances: 1, total_instances: 1, requires_ack: false },
  read_records: { id: 'read_records', type: 'tool', label: 'Read records', description: 'Query accounts, opportunities, contacts and custom objects.', enabled: false, active_instances: 0, total_instances: 1, requires_ack: false },
  record_created: { id: 'record_created', type: 'data_sync', label: 'Record created', description: 'Fires when a new row is inserted in a watched table.', enabled: true, active_instances: 1, total_instances: 2, requires_ack: true },
  write_records: { id: 'write_records', type: 'data_sync', label: 'Write records', description: 'Create and update records.', enabled: false, active_instances: 0, total_instances: 2, requires_ack: true },
});

let mockInstanceCapabilities = {};
const capsFor = (instanceId) => {
  if (!mockInstanceCapabilities[instanceId]) mockInstanceCapabilities[instanceId] = buildCapabilitySeed();
  return mockInstanceCapabilities[instanceId];
};

/** Test-only: reset the mutable per-instance capability state. */
export const __resetMockInstanceCapabilitiesForTests = () => {
  mockInstanceCapabilities = {};
};

/**
 * List this instance's capabilities with their toggle state (CT-4190).
 * TODO(CT-3904 / backend): replace with `GET .../instances/:id/capabilities`.
 */
export const listInstanceCapabilities = (instanceId) =>
  Promise.resolve(envelope(Object.values(capsFor(instanceId))));

/**
 * Enable/disable a capability for this instance (CT-4190) — immediate (optimistic).
 * TODO(CT-3904 / backend): replace with `PUT .../instances/:id/capabilities/:capId`.
 */
export const setInstanceCapability = (instanceId, capId, enabled) => {
  const caps = capsFor(instanceId);
  if (caps[capId]) {
    caps[capId].enabled = enabled;
    caps[capId].active_instances = enabled ? Math.max(1, caps[capId].active_instances) : Math.max(0, caps[capId].active_instances - 1);
  }
  return Promise.resolve(envelope({ id: capId, enabled }));
};

/** Prototype Access directory — workspaces / people / groups with Avatar colors. */
const ACCESS_WORKSPACES = [
  { id: 'sales', type: 'workspace', name: 'Sales Team', initials: 'ST', avatarClassName: 'bg-[#06B6D4]', member_count: 11, desc: 'Pipeline + outbound' },
  { id: 'eng', type: 'workspace', name: 'Engineering', initials: 'E', avatarClassName: 'bg-[#A78BFA]', member_count: 12, desc: 'Product + platform' },
  { id: 'cs', type: 'workspace', name: 'Customer Success', initials: 'CS', avatarClassName: 'bg-[#FDC700]', member_count: 7, desc: 'Support + onboarding' },
  { id: 'fin', type: 'workspace', name: 'Finance', initials: 'F', avatarClassName: 'bg-[#10B981]', member_count: 4, desc: 'Billing + reporting' },
  { id: 'mkt', type: 'workspace', name: 'Marketing', initials: 'M', avatarClassName: 'bg-[#EF4444]', member_count: 6, desc: 'Campaigns + lifecycle' },
];

const ACCESS_PEOPLE = [
  { id: 'tg', type: 'user', name: 'Thomas González', initials: 'TG', avatarClassName: 'bg-[#10b981]', role: 'CTO' },
  { id: 'mg', type: 'user', name: 'María González', initials: 'MG', avatarClassName: 'bg-[#7ed3f7]', role: 'Head of Data' },
  { id: 'es', type: 'user', name: 'Edgardo Sierra', initials: 'ES', avatarClassName: 'bg-[#cda5ff]', role: 'VP Engineering' },
  { id: 'sb', type: 'user', name: 'Sebastián Blandón', initials: 'SB', avatarClassName: 'bg-[#fbbf24]', role: 'Product Designer' },
  { id: 'dp', type: 'user', name: 'Diana Pérez', initials: 'DP', avatarClassName: 'bg-[#ff7d7d]', role: 'Solutions Architect' },
  { id: 'np', type: 'user', name: 'Naomi Park', initials: 'NP', avatarClassName: 'bg-[#34d39c]', role: 'Data Engineer' },
  { id: 'mc', type: 'user', name: 'Marcus Chen', initials: 'MC', avatarClassName: 'bg-[#a78bfa]', role: 'Platform Lead' },
  { id: 'sc', type: 'user', name: 'Sarah Chen', initials: 'SC', avatarClassName: 'bg-[#06B6D4]', role: 'Customer Success Lead' },
];

const ACCESS_GROUPS = [
  { id: 'sales-pod', type: 'group', name: 'Sales Pod', member_count: 5 },
  { id: 'leadership', type: 'group', name: 'Leadership Team', member_count: 5 },
  { id: 'engineering', type: 'group', name: 'Engineering', member_count: 7 },
  { id: 'customer-success', type: 'group', name: 'Customer Success', member_count: 4 },
];

const ACCESS_DIRECTORY = [...ACCESS_WORKSPACES, ...ACCESS_PEOPLE, ...ACCESS_GROUPS];

const entrySubtitle = (item) => {
  if (item.type === 'user') return item.role ?? '';
  if (item.type === 'workspace') return `${item.member_count} members · ${item.desc}`;
  return `${item.member_count} members`;
};

const toDirectoryOption = (item) => ({
  id: item.id,
  type: item.type,
  name: item.name,
  subtitle: entrySubtitle(item),
  initial: item.initials,
  avatarClassName: item.avatarClassName,
  member_count: item.member_count,
  desc: item.desc,
  role: item.role,
});

const toAccessEntry = (item, { id, level = 'use', granted_by = 'System', granted_at = null }) => ({
  id,
  subject_id: item.id,
  type: item.type,
  name: item.name,
  initials: item.initials,
  avatarClassName: item.avatarClassName,
  member_count: item.member_count,
  desc: item.desc,
  role: item.role,
  subtitle: entrySubtitle(item),
  level,
  granted_by,
  granted_at,
});

const reachOf = (entries) =>
  entries.reduce((sum, entry) => {
    if (entry.type === 'workspace' || entry.type === 'group') return sum + (entry.member_count ?? 0);
    return sum + 1;
  }, 0);

/** Per-instance access state (CT-4191) — mutable so grant/remove persist in-process. */
const buildAccessSeed = () => ({
  openToWorkspace: false,
  entries: [
    ...ACCESS_WORKSPACES.map((ws, i) =>
      toAccessEntry(ws, { id: `ac-ws-${i}`, granted_at: '2026-06-14T10:00:00.000Z' }),
    ),
    toAccessEntry(ACCESS_PEOPLE[0], { id: 'ac-p-tg', level: 'manage', granted_at: '2026-06-14T10:00:00.000Z' }),
    toAccessEntry(ACCESS_GROUPS[0], { id: 'ac-g-sales', granted_at: '2026-07-01T09:00:00.000Z' }),
    toAccessEntry(ACCESS_GROUPS[1], { id: 'ac-g-lead', granted_at: '2026-07-10T12:00:00.000Z' }),
  ],
});

let mockInstanceAccess = {};
const accessFor = (instanceId) => {
  if (!mockInstanceAccess[instanceId]) mockInstanceAccess[instanceId] = buildAccessSeed();
  return mockInstanceAccess[instanceId];
};

let accessIdCounter = 100;

/** Test-only: reset the mutable per-instance access state. */
export const __resetMockInstanceAccessForTests = () => {
  mockInstanceAccess = {};
  accessIdCounter = 100;
};

const grantedSubjectIds = (entries) => new Set(entries.map((e) => e.subject_id).filter(Boolean));

const directoryFor = (entries, query = '') => {
  const taken = grantedSubjectIds(entries);
  const q = query.trim().toLowerCase();
  return ACCESS_DIRECTORY.filter((item) => {
    if (taken.has(item.id)) return false;
    if (!q) return true;
    return item.name.toLowerCase().includes(q) || (item.role ?? '').toLowerCase().includes(q);
  }).map(toDirectoryOption);
};

/** List access entries for this instance (CT-4191). TODO(CT-3904 / backend). */
export const listInstanceAccess = (instanceId) => {
  const a = accessFor(instanceId);
  return Promise.resolve(
    envelope({
      open_to_workspace: a.openToWorkspace,
      entries: a.entries,
      workspace_total: ACCESS_WORKSPACES.length,
      reach_count: reachOf(a.entries),
      directory: directoryFor(a.entries),
    }),
  );
};

/** Grant access to an entity (CT-4191). TODO(CT-3904 / backend). */
export const grantInstanceAccess = (instanceId, entity, level) => {
  const a = accessFor(instanceId);
  const catalog = ACCESS_DIRECTORY.find((item) => item.id === entity.id) ?? entity;
  const entry = toAccessEntry(catalog, {
    id: `ac-${accessIdCounter++}`,
    level: level ?? 'use',
    granted_by: 'You',
    granted_at: null,
  });
  a.entries = [entry, ...a.entries];
  return Promise.resolve(envelope(entry));
};

/** Remove an access entry (CT-4191). TODO(CT-3904 / backend). */
export const removeInstanceAccess = (instanceId, entryId) => {
  const a = accessFor(instanceId);
  a.entries = a.entries.filter((e) => e.id !== entryId);
  return Promise.resolve(envelope({ id: entryId, removed: true }));
};

/** Impact preview before removing an access entry (CT-4191). */
export const getAccessRemovalImpact = (instanceId, entryId) => {
  const entry = accessFor(instanceId).entries.find((e) => e.id === entryId);
  const byType = {
    workspace: { workflows: 4, agents: 1, widgets: 2 },
    group: { workflows: 2, agents: 1, widgets: 0 },
    user: { workflows: 0, agents: 0, widgets: 0 },
  };
  const counts = byType[entry?.type] ?? { workflows: 0, agents: 0, widgets: 0 };
  return Promise.resolve(envelope({ name: entry?.name ?? '', ...counts }));
};

/**
 * Credential actions (CT-4194) — write-only credentials; Test/Rotate/Revoke.
 * TODO(CT-3904 / backend): replace with the real credential endpoints. The
 * frontend never receives secret values (write-only).
 */
export const testInstanceCredentials = (instanceId) =>
  Promise.resolve(envelope({ ok: true, checked_at: null }));

export const rotateInstanceCredentials = (instanceId, payload) =>
  // eslint-disable-next-line no-unused-vars -- payload is the write-only secret; never echoed back
  Promise.resolve(envelope({ rotated: true }));

export const revokeInstanceCredentials = (instanceId) =>
  Promise.resolve(envelope({ revoked: true }));
